/**
 * Workbench → Advanced → Schedulers.
 *
 * The one place a scheduler lives. The General Process and Report designers
 * show a DERIVED list of the schedulers that run them and link here; nothing
 * about a scheduler is stored on the object it runs, which is what stops the
 * two views ever disagreeing.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import api from '@/api/client'
import {
  deleteSchedule, listSchedules, runNow, setScheduleEnabled,
  type ScheduleView,
} from '@/api/schedules'
import {
  RunsPanel, schedulerColors as C, Chip, DriftNotice, Empty, StatusChip,
  cardStyle, formatRelative, formatWhen, labelStyle,
} from '@twasta/page-runtime/shell/jobs'
import ScheduleEditor, { blankSchedule } from './scheduler/ScheduleEditor'
import { makeSchedulerRuntime } from './scheduler/runtime'

type Pane = 'list' | 'edit' | 'runs'

export default function SchedulerDesignerPage() {
  const { project = '' } = useParams<{ project: string }>()
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()

  const [schedules, setSchedules] = useState<ScheduleView[] | null>(null)
  const [objects, setObjects] = useState<Record<string, string[]>>({})
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState<string>('')

  const selected = params.get('name') || ''
  const pane: Pane = (params.get('pane') as Pane)
    || (selected ? 'runs' : 'list')

  const go = useCallback((next: { name?: string; pane?: Pane }) => {
    const q = new URLSearchParams(params)
    if (next.name === undefined) { /* keep */ } else if (next.name) {
      q.set('name', next.name)
    } else { q.delete('name') }
    if (next.pane) q.set('pane', next.pane); else q.delete('pane')
    setParams(q, { replace: false })
  }, [params, setParams])

  const reload = useCallback(async () => {
    try {
      const res = await listSchedules(project)
      setSchedules(res.schedules || [])
      setError(null)
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } } }
      setError(err?.response?.data?.detail
        || (e instanceof Error ? e.message : 'Could not load schedulers'))
      setSchedules([])
    }
  }, [project])

  useEffect(() => { void reload() }, [reload])

  // The pickers need object names by type. The catalogue endpoint already
  // exists for the nav-lookup picker — reused rather than given a twin.
  useEffect(() => {
    let cancelled = false
    api.get('/designers/object-catalog',
            { params: { project, types: 'G,R,V,D,T,Y' } })
      .then((res) => {
        if (cancelled) return
        const grouped: Record<string, string[]> = {}
        for (const o of (res.data?.objects || []) as
             { obj_name: string; obj_type: string }[]) {
          (grouped[o.obj_type] ||= []).push(o.obj_name)
        }
        Object.values(grouped).forEach((v) => v.sort())
        setObjects(grouped)
      })
      .catch(() => { /* the editor still works, the picker is just empty */ })
    return () => { cancelled = true }
  }, [project])

  const current = useMemo(
    () => (schedules || []).find((s) => s.name === selected) || null,
    [schedules, selected])

  const doRunNow = useCallback(async (name: string) => {
    setBusy(name)
    try {
      await runNow(project, name)
      toast.success('Started — the run appears in the history in a moment')
      go({ name, pane: 'runs' })
      window.setTimeout(() => { void reload() }, 1200)
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } } }
      toast.error(err?.response?.data?.detail || 'Could not start the run')
    } finally {
      setBusy('')
    }
  }, [project, go, reload])

  const doToggle = useCallback(async (s: ScheduleView) => {
    try {
      await setScheduleEnabled(project, s.name, !s.enabled)
      await reload()
    } catch {
      toast.error('Could not change that')
    }
  }, [project, reload])

  const doDelete = useCallback(async (name: string) => {
    if (!window.confirm(
      `Delete the "${name}" scheduler? Its run history stays — that is a record `
      + 'of what happened, not part of the design.')) return
    try {
      await deleteSchedule(project, name)
      toast.success('Scheduler deleted')
      go({ name: '', pane: 'list' })
      await reload()
    } catch {
      toast.error('Could not delete it')
    }
  }, [project, go, reload])

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column',
      background: C.bg }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8,
        padding: '9px 20px', borderBottom: `1px solid ${C.line}`,
        background: C.panel, fontSize: '0.875rem', color: C.ink2 }}>
        <button type="button"
          onClick={() => navigate(`/workbench/${encodeURIComponent(project)}`)}
          style={{ background: 'none', border: 'none', cursor: 'pointer',
            color: C.ink2, display: 'flex', alignItems: 'center' }}>
          <span className="material-icons" style={{ fontSize: 19 }}>chevron_left</span>
        </button>
        <button type="button" onClick={() => go({ name: '', pane: 'list' })}
          style={{ background: 'none', border: 'none', cursor: 'pointer',
            color: C.ink2, padding: 0, fontSize: '0.875rem' }}>
          Advanced
        </button>
        <span style={{ color: C.faint }}>/</span>
        <strong style={{ color: C.ink, fontWeight: 600 }}>Schedulers</strong>
        {selected && (
          <>
            <span style={{ color: C.faint }}>/</span>
            <span style={{ color: C.ink }}>{current?.title || selected}</span>
          </>
        )}
      </div>

      <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: '16px 20px' }}>
        {error && (
          <div style={{ background: C.errWeak, border: `1px solid ${C.err}33`,
            borderRadius: 8, padding: '11px 13px', color: C.err,
            fontSize: '0.9125rem', marginBottom: 14 }}>{error}</div>
        )}

        {pane === 'edit' && (
          <ScheduleEditor project={project} objects={objects}
            initial={current || blankSchedule()}
            onSaved={(name) => { void reload(); go({ name, pane: 'runs' }) }}
            onCancel={() => go({ name: selected, pane: selected ? 'runs' : 'list' })} />
        )}

        {pane === 'runs' && current && (
          <SchedulerDetail project={project} schedule={current}
            busy={busy === current.name}
            onEdit={() => go({ name: current.name, pane: 'edit' })}
            onRunNow={() => void doRunNow(current.name)}
            onToggle={() => void doToggle(current)}
            onDelete={() => void doDelete(current.name)}
            onBack={() => go({ name: '', pane: 'list' })} />
        )}

        {pane === 'list' && (
          <SchedulerList project={project} schedules={schedules} busy={busy}
            onOpen={(name) => go({ name, pane: 'runs' })}
            onNew={() => go({ name: '', pane: 'edit' })}
            onRunNow={doRunNow} onToggle={doToggle} />
        )}
      </div>
    </div>
  )
}

// ── the list ───────────────────────────────────────────────────────────────

function SchedulerList({
  project, schedules, busy, onOpen, onNew, onRunNow, onToggle,
}: {
  project: string
  schedules: ScheduleView[] | null
  busy: string
  onOpen: (name: string) => void
  onNew: () => void
  onRunNow: (name: string) => void
  onToggle: (s: ScheduleView) => void
}) {
  const enabled = (schedules || []).filter((s) => s.enabled).length
  const failing = (schedules || []).filter(
    (s) => s.last_run?.status === 'failed')

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10,
        marginBottom: 3 }}>
        <h2 style={{ fontSize: '1.3125rem', fontWeight: 600, margin: 0 }}>
          Schedulers
        </h2>
        {schedules && (
          <span style={{ fontSize: '0.8625rem', color: C.ink2 }}>
            {schedules.length} defined · {enabled} enabled
          </span>
        )}
      </div>
      <p style={{ margin: '0 0 14px', fontSize: '0.8825rem', color: C.ink2,
        maxWidth: '82ch' }}>
        Unattended work: a process, a report, a visual or dashboard export, an
        action over a selection, or a maintenance function — on a cadence.
        Twasta owns the job and keeps the run record; IOFlow is the clock that
        calls it, and a downloaded runtime takes the same call from any cron.
      </p>

      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <div style={{ flex: 1 }} />
        <button type="button" className="btn btn-primary" onClick={onNew}>
          <span className="material-icons" style={{ fontSize: 17 }}>add</span>
          New scheduler
        </button>
      </div>

      <div style={{ ...cardStyle, overflow: 'hidden' }}>
        {schedules === null && (
          <div style={{ padding: '2rem', textAlign: 'center', color: C.ink2,
            fontSize: '0.9125rem' }}>Loading…</div>
        )}
        {schedules?.length === 0 && (
          <Empty title="No schedulers yet"
            hint="Create one to run a process, a report, a visual or a dashboard while nobody is watching — and to give it a run record when it does." />
        )}
        {!!schedules?.length && (
          <table style={{ borderCollapse: 'collapse', width: '100%',
            fontSize: '0.875rem' }}>
            <thead>
              <tr>
                {['Scheduler', 'What it runs', 'Cadence', 'Next run', 'Last run',
                  'State', ''].map((h) => (
                  <th key={h} style={{ textAlign: 'left', ...labelStyle,
                    padding: '7px 14px', borderBottom: `1px solid ${C.line}`,
                    background: C.s2 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {schedules.map((s) => (
                <tr key={s.name} onClick={() => onOpen(s.name)}
                  style={{ cursor: 'pointer' }}>
                  <td style={td(true)}>
                    <div>{s.title || s.name}</div>
                    <div style={{ fontFamily: 'var(--font-mono)', fontSize: '0.8125rem',
                      color: C.faint }}>{s.name}</div>
                  </td>
                  <td style={td()}>
                    {stepText(s)}
                    {s.step_count > 1 && (
                      <div style={{ fontSize: '0.8125rem', color: C.faint }}>
                        then {s.step_count - 1} more step
                        {s.step_count - 1 === 1 ? '' : 's'}
                      </div>
                    )}
                  </td>
                  <td style={td()}>{s.cadence_text}</td>
                  <td style={td()}>
                    {s.next_run ? (
                      <>
                        {formatWhen(s.next_run)}
                        <div style={{ fontSize: '0.8125rem', color: C.faint }}>
                          {formatRelative(s.next_run)}
                        </div>
                      </>
                    ) : <span style={{ color: C.faint }}>—</span>}
                  </td>
                  <td style={td()}>
                    {s.last_run
                      ? <StatusChip status={s.last_run.status} />
                      : <span style={{ color: C.faint }}>Never run</span>}
                  </td>
                  <td style={td()}>
                    <button type="button"
                      onClick={(e) => { e.stopPropagation(); onToggle(s) }}
                      style={{ background: 'none', border: 'none', padding: 0,
                        cursor: 'pointer' }}>
                      <Chip tone={s.enabled ? 'ok' : 'muted'}>
                        {s.enabled ? 'Enabled' : 'Disabled'}
                      </Chip>
                    </button>
                  </td>
                  <td style={{ ...td(), textAlign: 'right' }}>
                    <button type="button" title="Run now"
                      disabled={busy === s.name}
                      onClick={(e) => { e.stopPropagation(); onRunNow(s.name) }}
                      style={{ background: 'none', border: 'none',
                        cursor: 'pointer', color: C.accent, display: 'inline-flex' }}>
                      <span className="material-icons" style={{ fontSize: 19 }}>
                        play_arrow
                      </span>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {failing.length > 0 && (
        <div style={{ marginTop: 12, display: 'flex', gap: 9,
          padding: '10px 12px', borderRadius: 8, fontSize: '0.8625rem',
          background: C.warnWeak, border: `1px solid ${C.warn}33`, color: C.warn }}>
          <span className="material-icons" style={{ fontSize: 17 }}>warning_amber</span>
          <div>
            <strong>{failing.map((s) => s.title || s.name).join(', ')}</strong>
            {failing.length === 1 ? ' failed' : ' failed'} on its last run.
          </div>
        </div>
      )}
    </div>
  )
}

function td(strong = false) {
  return {
    padding: '9px 14px', borderBottom: `1px solid ${C.line}`,
    color: strong ? C.ink : C.ink2, fontWeight: strong ? 500 : 400,
    verticalAlign: 'top' as const,
  }
}

function stepText(s: ScheduleView) {
  const t = s.target
  const kind = t?.kind || ''
  const label = kind.charAt(0).toUpperCase() + kind.slice(1)
  return (
    <span>
      {label}{' '}
      <strong style={{ color: C.ink }}>{t?.obj_name || t?.function || ''}</strong>
    </span>
  )
}

// ── one scheduler ──────────────────────────────────────────────────────────

function SchedulerDetail({
  project, schedule, busy, onEdit, onRunNow, onToggle, onDelete, onBack,
}: {
  project: string
  schedule: ScheduleView
  busy: boolean
  onEdit: () => void
  onRunNow: () => void
  onToggle: () => void
  onDelete: () => void
  onBack: () => void
}) {
  const steps = [schedule.target, ...(schedule.then || [])]
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <button type="button" onClick={onBack} className="btn btn-secondary"
          style={{ padding: '0.35rem 0.7rem', fontSize: '0.8425rem' }}>
          All schedulers
        </button>
        <div style={{ flex: 1 }}>
          <h2 style={{ fontSize: '1.1875rem', fontWeight: 600, margin: 0 }}>
            {schedule.title || schedule.name}
          </h2>
          {schedule.description && (
            <div style={{ fontSize: '0.8625rem', color: C.ink2 }}>
              {schedule.description}
            </div>
          )}
        </div>
        <Chip tone={schedule.enabled ? 'ok' : 'muted'}>
          {schedule.enabled ? 'Enabled' : 'Disabled'}
        </Chip>
        <button type="button" className="btn btn-secondary" onClick={onToggle}
          style={{ fontSize: '0.8425rem', padding: '0.4rem 0.8rem' }}>
          {schedule.enabled ? 'Disable' : 'Enable'}
        </button>
        <button type="button" className="btn btn-secondary" onClick={onEdit}
          style={{ fontSize: '0.8425rem', padding: '0.4rem 0.8rem' }}>Edit</button>
        <button type="button" className="btn btn-primary" onClick={onRunNow}
          disabled={busy} style={{ fontSize: '0.8425rem', padding: '0.4rem 0.8rem' }}>
          <span className="material-icons" style={{ fontSize: 16 }}>play_arrow</span>
          {busy ? 'Starting…' : 'Run now'}
        </button>
        <button type="button" onClick={onDelete} title="Delete"
          style={{ background: 'none', border: 'none', cursor: 'pointer',
            color: C.ink2, display: 'flex' }}>
          <span className="material-icons" style={{ fontSize: 19 }}>delete_outline</span>
        </button>
      </div>

      <div style={{ display: 'grid',
        gridTemplateColumns: 'repeat(3, minmax(0,1fr))', gap: 1,
        background: C.line, border: `1px solid ${C.line}`, borderRadius: 10,
        overflow: 'hidden' }}>
        <Band label="Next run"
          value={schedule.next_run ? formatWhen(schedule.next_run) : 'Not scheduled'}
          sub={schedule.next_run ? formatRelative(schedule.next_run) : 'disabled'} />
        <Band label="Last run"
          value={schedule.last_run
            ? schedule.last_run.status.charAt(0).toUpperCase()
              + schedule.last_run.status.slice(1)
            : 'Never run'}
          sub={schedule.last_run ? formatWhen(schedule.last_run.queued_at) : ''} />
        <Band label="Cadence" value={schedule.cadence_text}
          sub={schedule.schedule?.cron_expression || ''} mono />
      </div>

      <DriftNotice drift={schedule.timezone_drift} />

      <div style={cardStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8,
          padding: '10px 14px', borderBottom: `1px solid ${C.line}` }}>
          <div style={{ fontSize: '0.875rem', fontWeight: 600, flex: 1 }}>
            What this job does
          </div>
          <Chip tone="muted">{steps.length} step{steps.length === 1 ? '' : 's'}</Chip>
        </div>
        {steps.map((step, i) => (
          <div key={i} style={{ display: 'flex', gap: 11, padding: '11px 14px',
            borderBottom: i < steps.length - 1 ? `1px solid ${C.line}` : 'none' }}>
            <div style={{ width: 22, height: 22, borderRadius: 999,
              flexShrink: 0, display: 'flex', alignItems: 'center',
              justifyContent: 'center', fontSize: '0.8125rem', fontWeight: 700,
              background: C.s2, color: C.ink2,
              border: `1px solid ${C.line2}`, marginTop: 1 }}>{i + 1}</div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: '0.9025rem', fontWeight: 600 }}>
                {step.kind} · <strong>{step.obj_name || step.function}</strong>
              </div>
              <div style={{ fontSize: '0.8225rem', color: C.ink2, marginTop: 1 }}>
                {argsText(step.args)}
                {step.format ? ` · ${step.format.toUpperCase()}` : ''}
                {step.deliver?.dms_folder
                  ? ` · files to ${step.deliver.dms_repo || '?'}/${step.deliver.dms_folder}`
                  : ''}
              </div>
            </div>
          </div>
        ))}
        <div style={{ padding: '9px 14px', borderTop: `1px solid ${C.line}`,
          fontSize: '0.8125rem', color: C.faint }}>
          {schedule.on_failure?.stop === false
            ? 'A failing step is recorded and the chain carries on.'
            : 'A failing step stops the chain; the steps behind it are recorded as skipped.'}
          {schedule.on_failure?.raise_work_item !== false
            && ' A failure raises a work item for the job owner.'}
        </div>
      </div>

      <RunsPanel runtime={makeSchedulerRuntime(project)}
        scheduleName={schedule.name} pollMs={4000} />
    </div>
  )
}

function Band({ label, value, sub, mono }: {
  label: string; value: string; sub?: string; mono?: boolean
}) {
  return (
    <div style={{ background: C.panel, padding: '12px 14px' }}>
      <div style={labelStyle}>{label}</div>
      <div style={{ fontSize: '1.125rem', fontWeight: 600, lineHeight: 1.25,
        marginTop: 3 }}>{value}</div>
      {sub && <div style={{ fontSize: '0.8125rem', color: C.ink2, marginTop: 2,
        fontFamily: mono ? 'var(--font-mono)' : undefined }}>{sub}</div>}
    </div>
  )
}

function argsText(args?: Record<string, unknown> | null) {
  const entries = Object.entries(args || {})
  if (!entries.length) return 'no criteria'
  return entries.map(([k, v]) => `${k} = ${String(v)}`).join(' · ')
}
